import { useState, useMemo, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import {
	ReactFlow,
	Background,
	Controls,
	MiniMap,
	useNodesState,
	useEdgesState,
	type Node,
	type Edge,
} from "@xyflow/react";
import {
	Search,
	Download,
	AlertTriangle,
	X,
	Eye,
	Table as TableIcon,
	Network,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import * as api from "@/services/api";

// Module color mapping
const moduleColors: Record<string, string> = {
	core: "#64748b",
	organization: "#3b82f6",
	hris: "#22c55e",
	ams: "#eab308",
	monitoring: "#f97316",
	kpi: "#ec4899",
	admin: "#ef4444",
	subscription: "#06b6d4",
	notifications: "#f59e0b",
	gamification: "#8b5cf6",
	appraisal: "#14b8a6",
};

interface DependencyChain {
	feature: string;
	depth: number;
	path: string[];
}

interface CircularDependency {
	features: string[];
	cycle: string;
}

export function DependencyGraph() {
	const [selectedFeature, setSelectedFeature] = useState<string | null>(null);
	const [search, setSearch] = useState("");
	const [selectedModule, setSelectedModule] = useState<string | null>(null);
	const [viewMode, setViewMode] = useState<"graph" | "table">("graph");

	const { data: dependencies, isLoading } = useQuery({
		queryKey: ["dependencies"],
		queryFn: api.getAllDependencies,
	});

	// Detect circular dependencies
	const circularDependencies = useMemo(() => {
		if (!dependencies) return [];
		const circles: CircularDependency[] = [];
		const visited = new Set<string>();
		const recursionStack = new Set<string>();

		const detectCycle = (feature: string, path: string[]): boolean => {
			if (recursionStack.has(feature)) {
				// Found a cycle
				const cycleStart = path.indexOf(feature);
				const cycle = [...path.slice(cycleStart), feature];
				circles.push({
					features: cycle,
					cycle: cycle.join(" → "),
				});
				return true;
			}

			if (visited.has(feature)) return false;

			visited.add(feature);
			recursionStack.add(feature);
			path.push(feature);

			const deps = dependencies.dependencyMap[feature] || [];
			for (const dep of deps) {
				if (dep.feature) {
					detectCycle(dep.feature, [...path]);
				}
			}

			recursionStack.delete(feature);
			return false;
		};

		dependencies.features.forEach((f) => {
			if (!visited.has(f.slug)) {
				detectCycle(f.slug, []);
			}
		});

		return circles;
	}, [dependencies]);

	// Get recursive dependencies for a feature
	const getRecursiveDependencies = useCallback(
		(featureSlug: string): DependencyChain[] => {
			if (!dependencies) return [];
			const chains: DependencyChain[] = [];
			const visited = new Set<string>();

			const traverse = (slug: string, depth: number, path: string[]) => {
				if (visited.has(slug)) return;
				visited.add(slug);

				chains.push({ feature: slug, depth, path: [...path] });

				const deps = dependencies.dependencyMap[slug] || [];
				deps.forEach((dep) => {
					if (dep.feature) {
						traverse(dep.feature, depth + 1, [...path, dep.feature]);
					}
				});
			};

			traverse(featureSlug, 0, [featureSlug]);
			return chains;
		},
		[dependencies]
	);

	// Get features that would break if this feature is disabled
	const getImpactedFeatures = useCallback(
		(featureSlug: string): string[] => {
			if (!dependencies) return [];
			const impacted = new Set<string>();

			const traverse = (slug: string) => {
				const dependents = dependencies.reverseDependencyMap[slug] || [];
				dependents.forEach((dep) => {
					if (!impacted.has(dep)) {
						impacted.add(dep);
						traverse(dep);
					}
				});
			};

			traverse(featureSlug);
			return Array.from(impacted);
		},
		[dependencies]
	);

	// Build nodes and edges from dependency data
	const { initialNodes, initialEdges } = useMemo(() => {
		if (!dependencies) return { initialNodes: [], initialEdges: [] };

		const nodes: Node[] = [];
		const edges: Edge[] = [];

		// Position calculation
		const moduleGroups: Record<string, typeof dependencies.features> = {};
		dependencies.features.forEach((f) => {
			const module = f.module || "other";
			if (!moduleGroups[module]) moduleGroups[module] = [];
			moduleGroups[module].push(f);
		});

		let yOffset = 0;
		Object.entries(moduleGroups).forEach(([module, features]) => {
			features.forEach((feature, i) => {
				const hasDeps =
					(dependencies.dependencyMap[feature.slug]?.length || 0) > 0;
				const hasDependents =
					(dependencies.reverseDependencyMap[feature.slug]?.length || 0) > 0;

				// Position: features with dependents on left, dependencies on right
				const xPos =
					hasDependents && !hasDeps ? 100 : hasDeps && !hasDependents ? 600 : 350;

				const isSelected = selectedFeature === feature.slug;
				const isRelated =
					selectedFeature &&
					(dependencies.dependencyMap[selectedFeature]?.some(
						(d) => d.feature === feature.slug
					) ||
						dependencies.reverseDependencyMap[selectedFeature]?.includes(
							feature.slug
						));

				nodes.push({
					id: feature.slug,
					type: "default",
					position: {
						x: xPos + (i % 3) * 180,
						y: yOffset + Math.floor(i / 3) * 80,
					},
					data: {
						label: (
							<div className="text-center">
								<div className="font-medium text-xs">
									{feature.ui?.label || feature.slug}
								</div>
								<div className="text-[10px] text-gray-500">{feature.slug}</div>
							</div>
						),
					},
					style: {
						background: isSelected
							? "#3b82f6"
							: isRelated
								? `${moduleColors[module] || "#6b7280"}40`
								: `${moduleColors[module] || "#6b7280"}20`,
						border: `2px solid ${isSelected ? "#1d4ed8" : moduleColors[module] || "#6b7280"}`,
						borderRadius: "8px",
						padding: "8px",
						width: 150,
						color: isSelected ? "white" : "inherit",
						cursor: "pointer",
					},
				});
			});
			yOffset += Math.ceil(features.length / 3) * 80 + 60;
		});

		// Create edges
		Object.entries(dependencies.dependencyMap).forEach(([feature, deps]) => {
			deps.forEach((dep) => {
				const isHighlighted =
					selectedFeature === feature || selectedFeature === dep.feature;
				edges.push({
					id: `${feature}-${dep.feature}`,
					source: feature,
					target: dep.feature,
					animated: isHighlighted,
					style: {
						stroke: isHighlighted ? "#3b82f6" : "#6b7280",
						strokeWidth: isHighlighted ? 2 : 1,
					},
					label: dep.action || "requires",
					labelStyle: { fontSize: 10 },
				});
			});
		});

		return { initialNodes: nodes, initialEdges: edges };
	}, [dependencies, selectedFeature]);

	const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
	const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

	// Update nodes when selection changes
	useMemo(() => {
		setNodes(initialNodes);
		setEdges(initialEdges);
	}, [initialNodes, initialEdges, setNodes, setEdges]);

	// Handle node click
	const onNodeClick = useCallback(
		(_event: React.MouseEvent, node: Node) => {
			setSelectedFeature(node.id === selectedFeature ? null : node.id);
		},
		[selectedFeature]
	);

	// Stats
	const stats = useMemo(() => {
		if (!dependencies) return { total: 0, withDeps: 0, roots: 0, leaves: 0 };

		const withDeps = Object.keys(dependencies.dependencyMap).length;
		const roots = dependencies.features.filter(
			(f) =>
				(dependencies.reverseDependencyMap[f.slug]?.length || 0) > 0 &&
				(dependencies.dependencyMap[f.slug]?.length || 0) === 0
		).length;
		const leaves = dependencies.features.filter(
			(f) =>
				(dependencies.dependencyMap[f.slug]?.length || 0) > 0 &&
				(dependencies.reverseDependencyMap[f.slug]?.length || 0) === 0
		).length;

		return {
			total: dependencies.features.length,
			withDeps,
			roots,
			leaves,
		};
	}, [dependencies]);

	// Get unique modules
	const modules = useMemo(() => {
		if (!dependencies) return [];
		const mods = new Set(dependencies.features.map((f) => f.module));
		return Array.from(mods).sort();
	}, [dependencies]);

	// Filtered features for table view
	const filteredFeatures = useMemo(() => {
		if (!dependencies) return [];
		return dependencies.features.filter((f) => {
			const matchesSearch =
				!search ||
				f.slug.toLowerCase().includes(search.toLowerCase()) ||
				f.ui?.label?.toLowerCase().includes(search.toLowerCase());
			const matchesModule = !selectedModule || f.module === selectedModule;
			return matchesSearch && matchesModule;
		});
	}, [dependencies, search, selectedModule]);

	// Export to CSV
	const handleExport = () => {
		if (!dependencies) return;

		const rows = [["Feature", "Module", "Dependencies", "Used By"]];
		dependencies.features.forEach((f) => {
			const deps = dependencies.dependencyMap[f.slug] || [];
			const dependents = dependencies.reverseDependencyMap[f.slug] || [];
			rows.push([
				f.slug,
				f.module || "",
				deps.map((d) => `${d.feature}:${d.action}`).join(", "),
				dependents.join(", "),
			]);
		});

		const csv = rows.map((row) => row.join(",")).join("\n");
		const blob = new Blob([csv], { type: "text/csv" });
		const url = URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = "feature-dependencies.csv";
		a.click();
		URL.revokeObjectURL(url);
	};

	// Inspector panel
	const InspectorPanel = () => {
		if (!selectedFeature || !dependencies) return null;

		const feature = dependencies.features.find((f) => f.slug === selectedFeature);
		if (!feature) return null;

		const recursiveDeps = getRecursiveDependencies(selectedFeature);
		const impactedFeatures = getImpactedFeatures(selectedFeature);
		const directDeps = dependencies.dependencyMap[selectedFeature] || [];
		const directDependents =
			dependencies.reverseDependencyMap[selectedFeature] || [];

		return (
			<Card className="sticky top-4">
				<CardHeader className="pb-3">
					<div className="flex items-center justify-between">
						<CardTitle className="text-lg">Dependency Inspector</CardTitle>
						<Button
							variant="ghost"
							size="sm"
							onClick={() => setSelectedFeature(null)}
						>
							<X className="w-4 h-4" />
						</Button>
					</div>
				</CardHeader>
				<CardContent className="space-y-4">
					{/* Feature Info */}
					<div>
						<h4 className="text-sm font-medium mb-2">Selected Feature</h4>
						<div className="p-3 rounded-lg bg-gray-50">
							<div className="font-medium">{feature.ui?.label || feature.slug}</div>
							<div className="text-xs text-gray-500 font-mono">{feature.slug}</div>
							<Badge className="mt-2" variant="outline">
								{feature.module}
							</Badge>
						</div>
					</div>

					{/* Stats */}
					<div className="grid grid-cols-2 gap-2">
						<div className="p-3 rounded-lg bg-blue-50">
							<div className="text-2xl font-bold text-blue-600">
								{directDeps.length}
							</div>
							<div className="text-xs text-blue-700">Direct Dependencies</div>
						</div>
						<div className="p-3 rounded-lg bg-green-50">
							<div className="text-2xl font-bold text-green-600">
								{directDependents.length}
							</div>
							<div className="text-xs text-green-700">Direct Dependents</div>
						</div>
						<div className="p-3 rounded-lg bg-purple-50">
							<div className="text-2xl font-bold text-purple-600">
								{recursiveDeps.length - 1}
							</div>
							<div className="text-xs text-purple-700">Total Dependencies</div>
						</div>
						<div className="p-3 rounded-lg bg-orange-50">
							<div className="text-2xl font-bold text-orange-600">
								{impactedFeatures.length}
							</div>
							<div className="text-xs text-orange-700">Impact Radius</div>
						</div>
					</div>

					{/* Dependency Tree */}
					{directDeps.length > 0 && (
						<div>
							<h4 className="text-sm font-medium mb-2">
								Direct Dependencies ({directDeps.length})
							</h4>
							<div className="space-y-1 max-h-40 overflow-y-auto">
								{directDeps.map((dep) => (
									<div
										key={`${dep.feature}:${dep.action}`}
										className="flex items-center justify-between p-2 rounded bg-gray-50 text-xs"
									>
										<span className="font-mono">{dep.feature}</span>
										<Badge variant="outline" className="text-[10px]">
											{dep.action}
										</Badge>
									</div>
								))}
							</div>
						</div>
					)}

					{/* Impact Analysis */}
					{impactedFeatures.length > 0 && (
						<div>
							<h4 className="text-sm font-medium mb-2 flex items-center gap-2">
								<AlertTriangle className="w-4 h-4 text-orange-600" />
								Impact Analysis
							</h4>
							<div className="p-3 rounded-lg bg-orange-50 text-sm text-orange-800">
								Disabling this feature would break{" "}
								<strong>{impactedFeatures.length}</strong> other feature(s)
							</div>
							<div className="space-y-1 max-h-32 overflow-y-auto mt-2">
								{impactedFeatures.slice(0, 10).map((feat) => (
									<div
										key={feat}
										className="text-xs font-mono p-2 rounded bg-gray-50"
									>
										{feat}
									</div>
								))}
								{impactedFeatures.length > 10 && (
									<div className="text-xs text-gray-500 text-center py-1">
										+{impactedFeatures.length - 10} more...
									</div>
								)}
							</div>
						</div>
					)}

					{/* Dependency Chain Depth */}
					{recursiveDeps.length > 1 && (
						<div>
							<h4 className="text-sm font-medium mb-2">Dependency Chain</h4>
							<div className="text-sm text-gray-600">
								Max depth:{" "}
								<strong>{Math.max(...recursiveDeps.map((d) => d.depth))}</strong>{" "}
								levels
							</div>
						</div>
					)}
				</CardContent>
			</Card>
		);
	};

	if (isLoading) {
		return (
			<div className="flex items-center justify-center h-64">
				<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
			</div>
		);
	}

	return (
		<div className="space-y-6">
			<div className="flex items-center justify-between">
				<div>
					<h1 className="text-3xl font-bold text-gray-900">
						Dependency Analysis
					</h1>
					<p className="text-gray-600 mt-2">
						Visual representation and analysis of feature dependencies
					</p>
				</div>
				<div className="flex items-center gap-2">
					<Button variant="outline" size="sm" onClick={handleExport}>
						<Download className="w-4 h-4 mr-2" />
						Export CSV
					</Button>
				</div>
			</div>

			{/* Circular Dependency Warnings */}
			{circularDependencies.length > 0 && (
				<Card className="border-orange-200 bg-orange-50">
					<CardHeader className="pb-3">
						<CardTitle className="text-lg flex items-center gap-2 text-orange-800">
							<AlertTriangle className="w-5 h-5" />
							Circular Dependencies Detected
						</CardTitle>
					</CardHeader>
					<CardContent>
						<div className="space-y-2">
							{circularDependencies.map((circ, idx) => (
								<div
									key={idx}
									className="p-3 rounded-lg bg-white border border-orange-200"
								>
									<div className="text-sm font-mono text-orange-900">
										{circ.cycle}
									</div>
								</div>
							))}
						</div>
					</CardContent>
				</Card>
			)}

			{/* Stats */}
			<div className="grid grid-cols-4 gap-4">
				<Card>
					<CardContent className="pt-4">
						<div className="text-2xl font-bold">{stats.total}</div>
						<p className="text-sm text-muted-foreground">Total Features</p>
					</CardContent>
				</Card>
				<Card>
					<CardContent className="pt-4">
						<div className="text-2xl font-bold">{stats.withDeps}</div>
						<p className="text-sm text-muted-foreground">With Dependencies</p>
					</CardContent>
				</Card>
				<Card>
					<CardContent className="pt-4">
						<div className="text-2xl font-bold text-green-600">
							{stats.roots}
						</div>
						<p className="text-sm text-muted-foreground">Root Features</p>
					</CardContent>
				</Card>
				<Card>
					<CardContent className="pt-4">
						<div className="text-2xl font-bold text-blue-600">
							{stats.leaves}
						</div>
						<p className="text-sm text-muted-foreground">Leaf Features</p>
					</CardContent>
				</Card>
			</div>

			{/* View Toggle */}
			<div className="flex items-center gap-4">
				<Tabs value={viewMode} defaultValue="graph" onValueChange={(v) => setViewMode(v as any)}>
					<TabsList>
						<TabsTrigger value="graph">
							<Network className="w-4 h-4 mr-2" />
							Graph View
						</TabsTrigger>
						<TabsTrigger value="table">
							<TableIcon className="w-4 h-4 mr-2" />
							Table View
						</TabsTrigger>
					</TabsList>
				</Tabs>

				{/* Search and Filter */}
				<div className="flex-1 flex items-center gap-2">
					<div className="relative flex-1 max-w-md">
						<Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
						<Input
							placeholder="Search features..."
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							className="pl-10"
						/>
					</div>
					<div className="flex gap-1">
						<Button
							variant={selectedModule === null ? "default" : "outline"}
							size="sm"
							onClick={() => setSelectedModule(null)}
						>
							All
						</Button>
						{modules.slice(0, 5).map((module) => (
							<Button
								key={module}
								variant={selectedModule === module ? "default" : "outline"}
								size="sm"
								onClick={() => setSelectedModule(module)}
							>
								{module}
							</Button>
						))}
					</div>
				</div>
			</div>

			{viewMode === "graph" ? (
				<div className="flex gap-6">
					{/* Graph View */}
					<div className="flex-1">
						{/* Legend */}
						<Card className="mb-4">
							<CardContent className="pt-4">
								<div className="flex flex-wrap gap-3">
									{Object.entries(moduleColors).map(([module, color]) => (
										<div key={module} className="flex items-center gap-2">
											<div
												className="w-4 h-4 rounded"
												style={{ backgroundColor: color }}
											/>
											<span className="text-sm capitalize">{module}</span>
										</div>
									))}
								</div>
							</CardContent>
						</Card>

						{/* Graph */}
						<Card className="overflow-hidden">
							<div style={{ height: 600 }}>
								<ReactFlow
									nodes={nodes}
									edges={edges}
									onNodesChange={onNodesChange}
									onEdgesChange={onEdgesChange}
									onNodeClick={onNodeClick}
									fitView
									attributionPosition="bottom-left"
								>
									<Background />
									<Controls />
									<MiniMap
										nodeColor={(node) => {
											const feature = dependencies?.features.find(
												(f) => f.slug === node.id
											);
											return moduleColors[feature?.module || "other"] || "#6b7280";
										}}
									/>
								</ReactFlow>
							</div>
						</Card>
					</div>

					{/* Inspector Panel */}
					{selectedFeature && (
						<div className="w-96">
							<InspectorPanel />
						</div>
					)}
				</div>
			) : (
				/* Table View */
				<Card>
					<CardContent className="pt-4">
						<div className="overflow-x-auto">
							<table className="w-full">
								<thead>
									<tr className="border-b">
										<th className="text-left py-3 px-4 font-medium text-sm">
											Feature
										</th>
										<th className="text-left py-3 px-4 font-medium text-sm">
											Module
										</th>
										<th className="text-left py-3 px-4 font-medium text-sm">
											Dependencies
										</th>
										<th className="text-left py-3 px-4 font-medium text-sm">
											Used By
										</th>
										<th className="text-center py-3 px-4 font-medium text-sm">
											Actions
										</th>
									</tr>
								</thead>
								<tbody>
									{filteredFeatures.map((feature) => {
										const deps = dependencies?.dependencyMap[feature.slug] || [];
										const dependents =
											dependencies?.reverseDependencyMap[feature.slug] || [];

										return (
											<tr
												key={feature.slug}
												className="border-b hover:bg-gray-50 cursor-pointer"
												onClick={() => setSelectedFeature(feature.slug)}
											>
												<td className="py-3 px-4">
													<div>
														<div className="font-medium text-sm">
															{feature.ui?.label || feature.slug}
														</div>
														<div className="text-xs text-gray-500 font-mono">
															{feature.slug}
														</div>
													</div>
												</td>
												<td className="py-3 px-4">
													<Badge variant="outline">{feature.module}</Badge>
												</td>
												<td className="py-3 px-4">
													{deps.length > 0 ? (
														<div className="flex flex-wrap gap-1">
															{deps.slice(0, 3).map((dep) => (
																<Badge
																	key={`${dep.feature}:${dep.action}`}
																	variant="secondary"
																	className="text-xs"
																>
																	{dep.feature}:{dep.action}
																</Badge>
															))}
															{deps.length > 3 && (
																<Badge variant="outline" className="text-xs">
																	+{deps.length - 3} more
																</Badge>
															)}
														</div>
													) : (
														<span className="text-sm text-gray-400">None</span>
													)}
												</td>
												<td className="py-3 px-4">
													{dependents.length > 0 ? (
														<div className="flex flex-wrap gap-1">
															{dependents.slice(0, 2).map((dep) => (
																<Badge
																	key={dep}
																	variant="outline"
																	className="text-xs"
																>
																	{dep}
																</Badge>
															))}
															{dependents.length > 2 && (
																<Badge variant="outline" className="text-xs">
																	+{dependents.length - 2} more
																</Badge>
															)}
														</div>
													) : (
														<span className="text-sm text-gray-400">None</span>
													)}
												</td>
												<td className="py-3 px-4 text-center">
													<Button
														variant="ghost"
														size="sm"
														onClick={(e) => {
															e.stopPropagation();
															setSelectedFeature(feature.slug);
															setViewMode("graph");
														}}
													>
														<Eye className="w-4 h-4" />
													</Button>
												</td>
											</tr>
										);
									})}
								</tbody>
							</table>

							{filteredFeatures.length === 0 && (
								<div className="text-center py-12 text-muted-foreground">
									No features match your search
								</div>
							)}
						</div>
					</CardContent>
				</Card>
			)}

			{/* Critical Features */}
			{dependencies && (
				<Card>
					<CardHeader>
						<CardTitle>Critical Features</CardTitle>
					</CardHeader>
					<CardContent>
						<div className="space-y-4">
							<div>
								<h4 className="text-sm font-medium mb-2">
									Most Depended On (Root Features)
								</h4>
								<div className="flex flex-wrap gap-2">
									{Object.entries(dependencies.reverseDependencyMap)
										.sort((a, b) => b[1].length - a[1].length)
										.slice(0, 5)
										.map(([feature, deps]) => (
											<Badge
												key={feature}
												variant="outline"
												className="cursor-pointer hover:bg-gray-100"
												onClick={() => {
													setSelectedFeature(feature);
													setViewMode("graph");
												}}
											>
												{feature}{" "}
												<span className="text-muted-foreground ml-1">
													({deps.length} dependents)
												</span>
											</Badge>
										))}
								</div>
							</div>
							<div>
								<h4 className="text-sm font-medium mb-2">
									Most Dependencies (Complex Features)
								</h4>
								<div className="flex flex-wrap gap-2">
									{Object.entries(dependencies.dependencyMap)
										.sort((a, b) => b[1].length - a[1].length)
										.slice(0, 5)
										.map(([feature, deps]) => (
											<Badge
												key={feature}
												variant="secondary"
												className="cursor-pointer hover:bg-gray-200"
												onClick={() => {
													setSelectedFeature(feature);
													setViewMode("graph");
												}}
											>
												{feature}{" "}
												<span className="text-muted-foreground ml-1">
													({deps.length} deps)
												</span>
											</Badge>
										))}
								</div>
							</div>
						</div>
					</CardContent>
				</Card>
			)}
		</div>
	);
}
